import React, {forwardRef, useImperativeHandle, useState, useEffect, ReactElement} from "react";
import {Select, Tag} from "antd";
import {Clazz} from "./BulkClassUpdate";
import PagedList from "@common/PagedList";
import Alert from "@common/Alert";

export interface ConfigureClassesRef {
    checkNextStep: () => void;
}

interface Props {
    classes: Record<string, Clazz>,
    schoolClasses: Record<number, object>,
    updateClassMatch: (classKey: string, matchId: string | number) => void,
    prepareData: () => void
}

const columns: object[] = [
    {
        title: 'Imported Class',
        dataIndex: 'importClass',
        key: 'importClass',
        width: '50%'
    },
    {
        title: 'Matched Class',
        dataIndex: 'matchClass',
        key: 'matchClass',
        render: (text: any) => {
            return <div className="flex">{text}</div>;
        },
        width: '50%'
    },
    {
        dataIndex: 'tag',
        key: 'tag',
        render: (_, record) => {
            const tag = record.tag();
            if (tag != null) return <Tag color="blue">{tag}</Tag>
            else return <></>
        }
    }
]

const ConfigureClasses = forwardRef<ConfigureClassesRef, Props>((props, ref) => {
    const {classes, schoolClasses, updateClassMatch, prepareData} = props;
    const [tableData, setTableData] = useState<object[]>([]);

    const handleClassMappingChange = (classKey: string, linkingId: number | string): void => {
        const newLinkingId = (linkingId === 'new-class' ? classKey : linkingId);
        updateClassMatch(classKey, newLinkingId);
    }

    const checkNextStep = (): void => {
        prepareData();
    }

    useEffect(() => {
        let newTableData: object[] = [];
        Object.entries(classes).map(([classKey, processedClass]: [string, Clazz]): any => {
            newTableData.push({
                key: classKey,
                importClass: processedClass.className,
                matchClass: (
                    <Select
                        style={{flex: "1"}}
                        showSearch={true}
                        defaultValue={
                            processedClass.className === processedClass.matchId && !processedClass.matchedClass ?
                                'new-class' :
                                processedClass.suggestedMatchId ?
                                processedClass.suggestedMatchId : processedClass.matchId
                        }
                        options={[
                            {value: 'new-class', label: 'New Class'},
                            ...Object.entries(schoolClasses)
                                .sort(([, a], [, b]) => a.className.localeCompare(b.className))
                                .map(([key, clazz]) => ({
                                    value: clazz.classId,
                                    label: clazz.className,
                                }))
                        ]}
                        onChange={(clazz) => handleClassMappingChange(classKey, clazz)}
                        filterOption={(input, option) => {
                            return option?.label.toLowerCase().includes(input.toLowerCase());
                        }}
                    />
                ),
                tag: (): string | null => {
                    if (processedClass.matchedClass && processedClass.id === processedClass.matchId) {
                        return "Exact Match"
                    } else {
                        return null;
                    }
                }
            });
        })
        setTableData(newTableData);
    }, [classes, schoolClasses]);

    useImperativeHandle(ref, () => ({
        checkNextStep
    }))

    return (
        <>
            <h1>Configure Classes</h1>
            <p>
                Configure the classes from your import to create new classes, or match them to an existing class.
            </p>
            <PagedList
                className="margin-t--l"
                columns={columns}
                dataSource={tableData}
            />
            {tableData.length == 0 && (
                <Alert
                    className="margin-t--l"
                    type="success"
                    showIcon
                    description="All classes have been matched to existing classes."
                />
            )}
        </>
    )
});

export default ConfigureClasses;